import type { GameStore } from '../../store/GameStore';
import { formatCurrency, formatNumber } from '../../i18n/i18n';

/**
 * Statistics panel — charts and graphs of company history.
 */
export class StatisticsPanel {
  private element: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private store: GameStore;
  private visible: boolean = false;

  constructor(parent: HTMLElement, store: GameStore) {
    this.store = store;

    this.element = document.createElement('div');
    this.element.className = 'panel';
    this.element.style.cssText = 'position:absolute;top:40px;right:0;bottom:0;width:320px;transform:translateX(100%);transition:transform var(--transition-normal);pointer-events:auto;z-index:10;';

    const header = document.createElement('div');
    header.className = 'panel__header';
    const title = document.createElement('span');
    title.textContent = 'Statistics';
    header.appendChild(title);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'btn btn--icon';
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => this.hide());
    header.appendChild(closeBtn);
    this.element.appendChild(header);

    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'panel__body';
    this.bodyEl.style.cssText = 'display:flex;flex-direction:column;gap:12px;';
    this.element.appendChild(this.bodyEl);

    parent.appendChild(this.element);
  }

  show(): void { this.visible = true; this.element.style.transform = 'translateX(0)'; this.render(); }
  hide(): void { this.visible = false; this.element.style.transform = 'translateX(100%)'; }
  toggle(): void { if (this.visible) this.hide(); else this.show(); }
  get isVisible(): boolean { return this.visible; }

  private drawMiniChart(values: number[], color: string, label: string, height: number = 40): HTMLDivElement {
    const container = document.createElement('div');
    container.style.cssText = `display:flex;flex-direction:column;gap:2px;`;

    const lbl = document.createElement('div');
    lbl.style.cssText = 'display:flex;justify-content:space-between;font-size:10px;';
    lbl.innerHTML = `<span style="color:var(--color-text-muted);">${label}</span><span style="font-family:var(--font-pixel);color:${color};">${values.length > 0 ? formatNumber(values[values.length - 1]!) : '0'}</span>`;
    container.appendChild(lbl);

    const canvas = document.createElement('canvas');
    canvas.width = 260;
    canvas.height = height;
    canvas.style.cssText = `width:100%;height:${height}px;border-radius:3px;background:var(--color-bg-primary);`;

    if (values.length > 1) {
      const ctx = canvas.getContext('2d')!;
      const max = Math.max(...values, 1);
      const w = canvas.width;
      const h = canvas.height;
      const step = w / (values.length - 1);

      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      for (let i = 0; i < values.length; i++) {
        const x = i * step;
        const y = h - (values[i]! / max) * (h - 4) - 2;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();

      // Fill under
      ctx.lineTo(w, h);
      ctx.lineTo(0, h);
      ctx.closePath();
      ctx.fillStyle = color.replace(')', ',0.15)').replace('rgb', 'rgba');
      ctx.fill();
    }

    container.appendChild(canvas);
    return container;
  }

  render(): void {
    if (!this.visible) return;
    this.bodyEl.innerHTML = '';

    const state = this.store.getState();

    // Revenue per product (bar-like via line chart)
    const revenueByProduct = state.products.map((p) => p.totalRevenue);
    this.bodyEl.appendChild(this.drawMiniChart(revenueByProduct, '#4ecca3', 'Revenue per Game'));

    // Review scores over time
    const reviewScores = state.products.map((p) => p.averageReview);
    this.bodyEl.appendChild(this.drawMiniChart(reviewScores, '#5b9bd5', 'Review Scores', 35));

    // Sales per product
    const salesByProduct = state.products.map((p) => p.totalSales);
    this.bodyEl.appendChild(this.drawMiniChart(salesByProduct, '#e88ab4', 'Sales per Game'));

    // Summary stats
    const section = document.createElement('div');
    section.style.cssText = 'display:flex;flex-direction:column;gap:2px;';

    const addStat = (label: string, value: string, color?: string) => {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;justify-content:space-between;font-size:11px;padding:2px 0;';
      row.innerHTML = `<span style="color:var(--color-text-muted);">${label}</span><span style="font-family:var(--font-pixel);${color ? `color:${color};` : ''}">${value}</span>`;
      section.appendChild(row);
    };

    addStat('Games Released', String(state.company.totalProductsReleased));
    addStat('Hall of Fame', String(state.company.hallOfFameCount), 'var(--color-warning)');
    addStat('Total Revenue', formatCurrency(state.company.totalRevenue), 'var(--color-funds)');
    addStat('Total Expenses', formatCurrency(state.company.totalExpenses), 'var(--color-danger)');
    const profit = state.company.totalRevenue - state.company.totalExpenses;
    addStat('Net Profit', formatCurrency(profit), profit >= 0 ? 'var(--color-success)' : 'var(--color-danger)');
    addStat('Fans', formatNumber(state.company.fans));
    addStat('Staff', `${state.staff.length} / ${state.office.maxStaff}`);

    if (state.products.length > 0) {
      const avgReview = state.products.reduce((s, p) => s + p.averageReview, 0) / state.products.length;
      addStat('Avg Review', avgReview.toFixed(1) + '/10');
      const best = [...state.products].sort((a, b) => b.averageReview - a.averageReview)[0]!;
      addStat('Best Game', `${best.name} (${best.averageReview.toFixed(1)})`);
    }

    this.bodyEl.appendChild(section);
  }

  destroy(): void { this.element.remove(); }
}
